import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';
import authHeader from '../services/auth-header';
import { API_BASE_URL } from '../common/config';
import { getScoreBorderClass, replaceUnderscoresAndUppercase } from '../common/helpers';

const FILTER_TYPES = ['all', 'risk', 'measure', 'owner', 'control', 'soa'];

const STATUS_COLORS = {
  implemented: 'search-status-green',
  'in-progress': 'search-status-blue',
  planned: 'search-status-yellow',
  dismissed: 'search-status-grey',
};

const TREATMENT_ICONS = {
  reduce: 'icon-arrow-down',
  accept: 'icon-check',
  avoid: 'icon-avoid',
  transfer: 'icon-transfer',
};

function highlight(text, query) {
  if (!query || !text) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? <mark key={i} className="search-highlight">{part}</mark> : part
  );
}

function ScoreBadge({ score, rangeBounds }) {
  if (!score) return null;
  const cls = getScoreBorderClass(score, rangeBounds);
  return <span className={`search-score-badge ${cls}`}>{score}</span>;
}

function StatusChip({ status }) {
  if (!status) return null;
  const cls = STATUS_COLORS[status] || 'search-status-grey';
  return <span className={`search-status-chip ${cls}`}>{replaceUnderscoresAndUppercase(status)}</span>;
}

function TypeBadge({ type }) {
  const labels = { risk: 'Risk', measure: 'Measure', owner: 'Owner', control: 'Control', soa: 'SoA' };
  return <span className={`search-type-badge search-type-${type}`}>{labels[type] || type}</span>;
}

function RiskResult({ item, query, rangeBounds, focused, onClick }) {
  return (
    <button className={`search-result-item ${focused ? 'focused' : ''}`} onClick={onClick}>
      <div className="search-result-main">
        <TypeBadge type="risk" />
        <span className="search-result-title">{highlight(item.description, query)}</span>
        <ScoreBadge score={item.actualScore} rangeBounds={rangeBounds} />
      </div>
      <div className="search-result-meta">
        {item.owner && <span className="search-meta-owner"><i className="icon-user" /> {item.owner}</span>}
        {item.treatment && (
          <span className="search-meta-treatment">
            <i className={TREATMENT_ICONS[item.treatment] || 'icon-Maatregelen'} />
            {' '}{replaceUnderscoresAndUppercase(item.treatment)}
          </span>
        )}
      </div>
    </button>
  );
}

function MeasureResult({ item, query, focused, onClick }) {
  return (
    <button className={`search-result-item ${focused ? 'focused' : ''}`} onClick={onClick}>
      <div className="search-result-main">
        <TypeBadge type="measure" />
        <span className="search-result-title">{highlight(item.measureText, query)}</span>
        <StatusChip status={item.measureStatus} />
      </div>
      <div className="search-result-meta">
        <span className="search-meta-context"><i className="icon-Risico" /> {item.riskDescription}</span>
        {item.measureOwner && <span className="search-meta-owner"><i className="icon-user" /> {item.measureOwner}</span>}
        {item.category?.length > 0 && (
          <span className="search-meta-categories">
            {item.category.slice(0, 3).map((c, i) => (
              <span key={i} className="search-category-tag">{typeof c === 'string' ? c : c?.value}</span>
            ))}
            {item.category.length > 3 && <span className="search-category-tag">+{item.category.length - 3}</span>}
          </span>
        )}
      </div>
    </button>
  );
}

function OwnerResult({ item, query, focused, onClick }) {
  return (
    <button className={`search-result-item ${focused ? 'focused' : ''}`} onClick={onClick}>
      <div className="search-result-main">
        <TypeBadge type="owner" />
        <span className="search-result-title">{highlight(item.owner, query)}</span>
        <span className="search-owner-count">{item.riskCount} risk{item.riskCount !== 1 ? 's' : ''}</span>
      </div>
    </button>
  );
}

function ControlResult({ item, query, focused, onClick }) {
  return (
    <button className={`search-result-item ${focused ? 'focused' : ''}`} onClick={onClick}>
      <div className="search-result-main">
        <TypeBadge type="control" />
        <span className="search-control-id">{item.id}</span>
        <span className="search-result-title">{highlight(item.name, query)}</span>
        {item.usageCount > 0 && (
          <span className="search-usage-count"><i className="icon-link" /> {item.usageCount}</span>
        )}
      </div>
      {item.controlTxt && (
        <div className="search-result-meta">
          <span className="search-meta-context">{item.controlTxt.slice(0, 120)}{item.controlTxt.length > 120 ? '…' : ''}</span>
        </div>
      )}
    </button>
  );
}

function SoaResult({ item, query, focused, onClick }) {
  const applicableLabel = item.applicable ? 'Applicable' : 'Not applicable';
  const applicableCls = item.applicable ? 'search-status-green' : 'search-status-grey';
  return (
    <button className={`search-result-item ${focused ? 'focused' : ''}`} onClick={onClick}>
      <div className="search-result-main">
        <TypeBadge type="soa" />
        <span className="search-control-id">{item.id}</span>
        <span className="search-result-title">{highlight(item.name, query)}</span>
        <span className={`search-status-chip ${applicableCls}`}>{applicableLabel}</span>
      </div>
      {item.justification && (
        <div className="search-result-meta">
          <span className="search-meta-context">{item.justification.slice(0, 100)}{item.justification.length > 100 ? '…' : ''}</span>
        </div>
      )}
    </button>
  );
}

function GroupHeader({ icon, label, count }) {
  return (
    <div className="search-group-header">
      <i className={icon} />
      <span>{label}</span>
      <span className="search-group-count">{count}</span>
    </div>
  );
}

const SearchModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const { currentOrg } = useAppContext();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [focusedIndex, setFocusedIndex] = useState(0);
  const debounceRef = useRef(null);

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults(null);
      setActiveFilter('all');
      setFocusedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const doSearch = useCallback((q) => {
    if (!q || q.trim().length < 2) {
      setResults(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const headers = authHeader();
    headers['Content-Type'] = 'application/json';
    fetch(`${API_BASE_URL}/api/risks/search/global/${currentOrg.id}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: q,
        securityStandard: currentOrg.securityStandard || 'ISO27001',
        language: i18n.language || 'en',
      }),
    })
      .then(r => r.json())
      .then(data => {
        setResults(data);
        setFocusedIndex(0);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [currentOrg, i18n.language]);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  // Build flat list of all visible results for keyboard nav
  const flatResults = React.useMemo(() => {
    if (!results) return [];
    const all = [
      ...(results.risks || []),
      ...(results.measures || []),
      ...(results.owners || []),
      ...(results.controls || []),
      ...(results.soa || []),
    ];
    if (activeFilter === 'all') return all;
    return all.filter(r => r.type === activeFilter);
  }, [results, activeFilter]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(i => Math.min(i + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flatResults.length > 0) {
      e.preventDefault();
      handleResultClick(flatResults[focusedIndex]);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // cycle through filters
      const idx = FILTER_TYPES.indexOf(activeFilter);
      setActiveFilter(FILTER_TYPES[(idx + 1) % FILTER_TYPES.length]);
    }
  };

  const handleResultClick = (item) => {
    onClose();
    if (!item) return;
    if (item.type === 'risk') {
      navigate(`/viewrisk?riskId=${item.id}`);
    } else if (item.type === 'measure') {
      navigate(`/viewrisk?riskId=${item.id}#measures`);
    } else if (item.type === 'owner') {
      navigate(`/risks?riskOwnerFilter=${encodeURIComponent(item.owner)}&showFilters=true`);
    } else if (item.type === 'control' || item.type === 'soa') {
      navigate(`/measures?categoryFilter=${encodeURIComponent(item.id)}`);
    }
  };

  if (!isOpen) return null;

  const showRisks = results?.risks?.length > 0 && (activeFilter === 'all' || activeFilter === 'risk');
  const showMeasures = results?.measures?.length > 0 && (activeFilter === 'all' || activeFilter === 'measure');
  const showOwners = results?.owners?.length > 0 && (activeFilter === 'all' || activeFilter === 'owner');
  const showControls = results?.controls?.length > 0 && (activeFilter === 'all' || activeFilter === 'control');
  const showSoa = results?.soa?.length > 0 && (activeFilter === 'all' || activeFilter === 'soa');
  const hasResults = showRisks || showMeasures || showOwners || showControls || showSoa;
  const showEmpty = results && !hasResults && !isLoading;

  // Map flat index to item for focus tracking
  let flatIdx = 0;
  const getFlatIdx = () => flatIdx++;

  return (
    <div className="search-modal-overlay" onClick={onClose}>
      <div className="search-modal" onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
        {/* Search input */}
        <div className="search-modal-input-row">
          <i className={`${isLoading ? 'icon-spinner fa-spin' : 'icon-search'} search-modal-icon`} />
          <input
            ref={inputRef}
            className="search-modal-input"
            type="text"
            value={query}
            onChange={handleQueryChange}
            placeholder={t('search_global_placeholder', 'Search risks, measures, owners, controls…')}
            autoComplete="off"
          />
          {query && (
            <button className="search-modal-clear" onClick={() => { setQuery(''); setResults(null); inputRef.current?.focus(); }}>
              <i className="icon-close" />
            </button>
          )}
          <kbd className="search-modal-esc" onClick={onClose}>esc</kbd>
        </div>

        {/* Filter chips */}
        {results && (
          <div className="search-filter-bar">
            {FILTER_TYPES.map(type => {
              let count = 0;
              if (type === 'all') count = results.total || 0;
              else if (type === 'risk') count = results.risks?.length || 0;
              else if (type === 'measure') count = results.measures?.length || 0;
              else if (type === 'owner') count = results.owners?.length || 0;
              else if (type === 'control') count = results.controls?.length || 0;
              else if (type === 'soa') count = results.soa?.length || 0;
              if (type !== 'all' && count === 0) return null;
              const labels = { all: t('search_filter_all', 'All'), risk: t('search_filter_risks', 'Risks'), measure: t('search_filter_measures', 'Measures'), owner: t('search_filter_owners', 'Owners'), control: t('search_filter_controls', 'Controls'), soa: 'SoA' };
              return (
                <button
                  key={type}
                  className={`search-filter-chip ${activeFilter === type ? 'active' : ''}`}
                  onClick={() => { setActiveFilter(type); setFocusedIndex(0); }}
                >
                  {labels[type]}
                  {count > 0 && <span className="search-filter-count">{count}</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Results */}
        <div className="search-results-list">
          {!query && (
            <div className="search-empty-state">
              <i className="icon-search" />
              <p>{t('search_hint', 'Start typing to search across your risk register')}</p>
              <div className="search-hint-grid">
                <div className="search-hint-item"><i className="icon-Risico" /> Risks</div>
                <div className="search-hint-item"><i className="icon-Maatregelen" /> Measures</div>
                <div className="search-hint-item"><i className="icon-user" /> Owners</div>
                <div className="search-hint-item"><i className="icon-shield" /> Controls</div>
                <div className="search-hint-item"><i className="icon-SoA" /> SoA</div>
              </div>
              <div className="search-shortcuts">
                <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
                <span><kbd>↵</kbd> open</span>
                <span><kbd>Tab</kbd> filter</span>
                <span><kbd>Esc</kbd> close</span>
              </div>
            </div>
          )}

          {showEmpty && (
            <div className="search-empty-state">
              <i className="icon-search" />
              <p>{t('no_results', 'No results found.')}</p>
              <span className="search-empty-hint">Try different keywords or remove the filter</span>
            </div>
          )}

          {showRisks && (
            <div className="search-group">
              <GroupHeader icon="icon-Risico" label={t('search_filter_risks', 'Risks')} count={results.risks.length} />
              {results.risks.map(item => {
                const fi = getFlatIdx();
                return (
                  <RiskResult
                    key={item.id + '-risk'}
                    item={item}
                    query={query}
                    rangeBounds={currentOrg.rangeBounds}
                    focused={focusedIndex === fi}
                    onClick={() => handleResultClick(item)}
                  />
                );
              })}
            </div>
          )}

          {showMeasures && (
            <div className="search-group">
              <GroupHeader icon="icon-Maatregelen" label={t('search_filter_measures', 'Measures')} count={results.measures.length} />
              {results.measures.map((item, idx) => {
                const fi = getFlatIdx();
                return (
                  <MeasureResult
                    key={item.measureText + idx}
                    item={item}
                    query={query}
                    focused={focusedIndex === fi}
                    onClick={() => handleResultClick(item)}
                  />
                );
              })}
            </div>
          )}

          {showOwners && (
            <div className="search-group">
              <GroupHeader icon="icon-users" label={t('search_filter_owners', 'Owners')} count={results.owners.length} />
              {results.owners.map((item, idx) => {
                const fi = getFlatIdx();
                return (
                  <OwnerResult
                    key={item.owner + idx}
                    item={item}
                    query={query}
                    focused={focusedIndex === fi}
                    onClick={() => handleResultClick(item)}
                  />
                );
              })}
            </div>
          )}

          {showControls && (
            <div className="search-group">
              <GroupHeader icon="icon-shield" label={t('search_filter_controls', 'Controls')} count={results.controls.length} />
              {results.controls.map((item, idx) => {
                const fi = getFlatIdx();
                return (
                  <ControlResult
                    key={item.id + idx}
                    item={item}
                    query={query}
                    focused={focusedIndex === fi}
                    onClick={() => handleResultClick(item)}
                  />
                );
              })}
            </div>
          )}

          {showSoa && (
            <div className="search-group">
              <GroupHeader icon="icon-SoA" label="SoA" count={results.soa.length} />
              {results.soa.map((item, idx) => {
                const fi = getFlatIdx();
                return (
                  <SoaResult
                    key={item.id + idx}
                    item={item}
                    query={query}
                    focused={focusedIndex === fi}
                    onClick={() => handleResultClick(item)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
